import { EmberTutorialPrompt } from "game-common/models"
import { Container, Graphics, InteractionEvent, Rectangle, Text } from "pixi.js"
import { PixelLayer } from "../intro/pixel_layer"
import { ART_PX, PixelBuffer, bayer, mix, rgb } from "../intro/pixel_raster"
import { disposeTextTextures } from "../text/dispose_text"
import { beginPose, CARD, cardPose, laterPose, shownAt } from "./chapter_card/card_motion"
import { LAYER_SKY, NightView, pack, WORDS_BOX } from "./chapter_card/night_view"
import { bakeInBrowser, fitTitle, paintRule, paintTitle, Rasterize, TitleRaster, titleGlow } from "./chapter_card/title_letters"
import { PHONE_BELOW } from "./character_creation/creation_layout"
import { TEXT, TEXT_INK, TEXT_SOFT } from "./character_creation/panel_art"
import { capsText, text } from "./character_creation/widgets"

// The chapter's title card: a full-viewport cover the server holds the player behind until Begin.
//
// It is a picture in the login intro's pixel art: one view over the forest the camp is hidden in, at
// an hour that moves on from chapter to chapter, dusk at the first and first light at the ninth
// (chapter_card/night_view.ts). Nothing in it acts out the chapter; the words do that. The title is
// in pixel letters baked from Jost like the "samsara" wordmark, with a gold lens rule under it; the
// overline and the line under the rule are Jost, as on the character builder. Begin is the
// builder's Journey Onward plate, bottom right, and answers from the first frame.
//
// The picture, title, rule and plates are one buffer of art pixels on a PixelLayer, ART_PX logical
// pixels to the art pixel, so its pixels are the intro's size. Poses are pure functions of the
// card's clock (chapter_card/card_motion.ts); animate() advances it from GuiManager.updateTimer.
//
// A phone shows the stage at about half desktop size, too small to read or tap, so there the words
// and the plates are drawn larger (wordScale), to the size they show at on the smallest desktop
// screen (the character builder's PHONE_BELOW). The picture and the title keep their pixel size: the
// picture already fills the screen, and the title still reads.

export type AnnouncementCue = "reveal" | "launch"

/** The plates, in art pixels: Begin is the builder's Journey Onward, a little shorter. */
export const BEGIN_PLATE = { w: 58, h: 15 }
export const LATER_PLATE = { w: 44, h: 15 }
const PLATE_GAP = 5
/** From the screen's right and bottom edges, in art pixels. */
const PLATE_EDGE = 13
/** Narrower than this (logical px, at the words' scale), Later goes under Begin. */
const STACK_BELOW = 420
/** Where the capitals of the title start, in art pixels from the top of the authored frame. */
const TITLE_CAP_Y = 36
/** The widest a title may be, in art pixels; a longer one steps down a size. */
const TITLE_MAX_W = 268
/** The words' sizes and the line's widest wrap at desktop scale, in logical px. */
const OVERLINE_SIZE = 12
const LINE_SIZE = 16
const LINE_HEIGHT = 21
const LINE_WRAP = 400
const CAPTION_SIZE = 12
/** The most the words and plates are drawn larger on a phone. */
const MAX_WORD_SCALE = 2
const OVERLINE_COLOR = 0xd8b77c
const LINE_COLOR = 0xd9cbb3
const BLACK = pack([5, 6, 10])
/** The character builder's plate colours (character_creation/panel_art.ts). */
const PLATE = {
    amber: rgb(0xf2a94e),
    amberHover: mix(rgb(0xf2a94e), rgb(0xfff6e8), 0.12),
    amberHi: rgb(0xffd27a),
    amberLo: rgb(0xb8742e),
    amberLine: rgb(0x4b2b13),
    tile: rgb(0x10141f),
    tileHover: rgb(0x171c2b),
    edge: rgb(0x3d3748),
    bronze: rgb(0x744b30),
    light: rgb(0xfff1b0),
}
// One frame never advances the card more than this, so a loading hitch under the cover cannot
// swallow the arrival.
const MAX_STEP_MS = 50
/** At rest the picture only needs redrawing this often (firelight, smoke, the moon on the water). */
const REST_REPAINT_MS = 33

const NUMBER_WORDS = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"]

/**
 * How much larger than on desktop to draw the words and plates for a screen showing `screenPerStage`
 * screen pixels to the stage pixel: enough that they are never smaller on screen than at PHONE_BELOW.
 */
export const wordScaleFor = (screenPerStage?: number): number =>
    screenPerStage ? Math.round(Math.min(MAX_WORD_SCALE, Math.max(1, PHONE_BELOW / screenPerStage)) * 100) / 100 : 1

/** "CHAPTER TWO": the overline spells the chapter's number. */
export const chapterOverline = (chapter: number) => `CHAPTER ${(NUMBER_WORDS[chapter] || String(chapter)).toUpperCase()}`

interface Plate {
    /** In art pixels. */
    x: number
    y: number
    w: number
    h: number
    /** Its size at desktop scale. */
    size: { w: number; h: number }
    hit: Container
    hovered: boolean
}

export class ChapterAnnouncement extends Container {
    private layer = new PixelLayer(false)
    private buffer?: PixelBuffer
    private view?: NightView
    private title?: TitleRaster
    private glow?: Float32Array
    private titleAt = { x: 0, y: 0 }
    private rule = { cx: 0, y: 0, half: 0 }
    private words = new Container()
    private overline?: Text
    private line?: Text
    private captions = new Container()
    /** Begin's caption lit (ink on amber) and unlit (cream on the dark plate), each clipped to its part. */
    private beginLit?: Text
    private beginUnlit?: Text
    private litClip = new Graphics()
    private unlitClip = new Graphics()
    private laterCaption?: Text
    private button?: Plate
    private laterButton?: Plate
    private chapter?: number
    private titleText?: string
    private body = ""
    /** How much larger than on desktop the words and plates are drawn: 1, or up to 2 on a phone. */
    private wordScale = 1
    /** The screen pixels per stage pixel GuiManager last measured, kept for open()'s own layout. */
    private screenPerStage?: number
    private exit?: { kind: "begin" | "later"; ms: number }
    private ms = 0
    /** The picture's own clock: the firelight and smoke keep moving whatever the card is doing. */
    private clock = 0
    private paintedAt = -Infinity
    private bounds = new Rectangle(0, 0, 800, 461)
    private laidOut?: string
    private cueRevealed = false

    constructor(
        private onBegin: (chapter: number) => void,
        private canBegin: () => boolean = () => true,
        private onLater?: (chapter: number) => void,
        private onCue: (cue: AnnouncementCue) => void = () => undefined,
        private rasterize: Rasterize = bakeInBrowser,
    ) {
        super()
        this.addChild(this.layer.sprite, this.words, this.captions, this.litClip, this.unlitClip)
        this.interactive = true
        // A click on the card while it is still arriving jumps to the finished card. Begin and Later
        // answer from the first frame; they stop their own presses reaching here.
        this.on("pointerdown", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (this.visible && !this.exit && this.ms < CARD.restMs) this.ms = CARD.restMs
        })
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.visible = false
    }

    open = (prompt: EmberTutorialPrompt) => {
        // Login and camp entry can both announce the same chapter. Preserve its current state.
        if (this.visible && this.chapter === prompt.announcementChapter) return
        this.clearContent()
        this.chapter = prompt.announcementChapter
        this.titleText = prompt.title
        this.body = prompt.body
        this.exit = undefined
        this.ms = 0
        this.cueRevealed = false
        this.alpha = 1
        this.visible = true
        this.button = this.makePlate(BEGIN_PLATE, this.begin)
        if ((this.chapter || 1) >= 2) this.laterButton = this.makePlate(LATER_PLATE, this.maybeLater)
        // Laying out sets the words, at the scale the screen asks for.
        this.laidOut = undefined
        this.resize(this.bounds.width, this.bounds.height, this.bounds.x, this.bounds.y, this.screenPerStage)
        this.pose()
        // The cover is opaque on the very first frame, before room loading can reveal the world.
        this.paint()
    }

    /** A plate's hit area: its own pointer handling, so a press on it is never a skip. */
    private makePlate = (size: { w: number; h: number }, onPress: () => void): Plate => {
        const hit = new Container()
        const plate: Plate = { x: 0, y: 0, ...size, size, hit, hovered: false }
        hit.interactive = true
        hit.buttonMode = true
        hit.on("pointerdown", (event: InteractionEvent) => event?.stopPropagation())
        hit.on("pointertap", (event: InteractionEvent) => {
            event?.stopPropagation()
            onPress()
        })
        hit.on("pointerover", () => (plate.hovered = true))
        for (const event of ["pointerout", "pointerupoutside", "pointercancel"]) hit.on(event, () => (plate.hovered = false))
        this.addChild(hit)
        return plate
    }

    /** The overline, the line and the plates' captions, at the words' scale. */
    private setWords = () => {
        for (const label of [this.overline, this.line, this.beginLit, this.beginUnlit, this.laterCaption]) disposeTextTextures(label)
        const k = this.wordScale
        this.overline = capsText(chapterOverline(this.chapter || 1), { size: OVERLINE_SIZE * k, weight: "500", color: OVERLINE_COLOR })
        this.overline.anchor.set(0.5, 1)
        this.line = text(this.body, { display: true, size: LINE_SIZE * k, weight: "400", color: LINE_COLOR, wrap: LINE_WRAP * k })
        this.line.style.align = "center"
        this.line.style.lineHeight = LINE_HEIGHT * k
        this.line.anchor.set(0.5, 0)
        this.words.addChild(this.overline, this.line)
        this.beginLit = text("Begin  ▸", { display: true, size: CAPTION_SIZE * k, weight: "600", color: TEXT_INK })
        this.beginUnlit = text("Begin  ▸", { display: true, size: CAPTION_SIZE * k, weight: "600", color: TEXT_SOFT })
        this.beginLit.mask = this.litClip
        this.beginUnlit.mask = this.unlitClip
        this.captions.addChild(this.beginLit, this.beginUnlit)
        this.laterCaption = undefined
        if (this.laterButton) {
            this.laterCaption = text("Later", { display: true, size: CAPTION_SIZE * k, weight: "500", color: TEXT_SOFT })
            this.captions.addChild(this.laterCaption)
        }
        for (const caption of [this.beginLit, this.beginUnlit, this.laterCaption]) caption?.anchor.set(0.5, 0.5)
    }

    private clearContent = () => {
        for (const label of [this.overline, this.line, this.beginLit, this.beginUnlit, this.laterCaption]) disposeTextTextures(label)
        for (const plate of [this.button, this.laterButton]) plate?.hit.destroy()
        this.overline = this.line = this.beginLit = this.beginUnlit = this.laterCaption = undefined
        this.button = this.laterButton = undefined
        this.title = this.glow = undefined
    }

    /**
     * Called every frame with the screen in local coordinates, and how many screen pixels a stage pixel
     * is shown at (a phone's are fewer than one); lays out only when something changed.
     */
    resize = (width: number, height: number, x = 0, y = 0, screenPerStage?: number) => {
        this.bounds = new Rectangle(x, y, width, height)
        this.hitArea = this.bounds
        this.screenPerStage = screenPerStage
        const scale = wordScaleFor(screenPerStage)
        const key = `${x}|${y}|${width}|${height}|${scale}|${this.chapter}`
        if (this.laidOut === key) return
        this.laidOut = key
        if (this.chapter === undefined) return
        if (!this.overline || scale !== this.wordScale) {
            this.wordScale = scale
            this.setWords()
        }
        // The picture covers the screen on whole art pixels, in the authored frame's coordinates, so a
        // wider or taller screen just shows more sky and ridge.
        const x0 = Math.floor(x / ART_PX), y0 = Math.floor(y / ART_PX)
        const w = Math.ceil((x + width) / ART_PX) - x0, h = Math.ceil((y + height) / ART_PX) - y0
        this.view = new NightView(this.chapter, { x0, y0, w, h })
        this.buffer = new PixelBuffer(w, h, x0, y0)
        this.layer.place(x0, y0, w, h)
        this.layout(width, x0, y0, w, h)
        this.paintedAt = -Infinity
        this.paint()
    }

    private layout = (width: number, x0: number, y0: number, w: number, h: number) => {
        const cx = x0 + Math.round(w / 2)
        const maxW = Math.min(TITLE_MAX_W, w - 24)
        if (!this.title || this.title.w > maxW) this.title = this.titleText ? fitTitle(this.titleText, maxW, this.rasterize) : undefined
        this.glow = this.title ? titleGlow(this.title) : undefined
        const capTop = this.title?.capTop ?? 2
        const base = this.title?.base ?? 22
        const titleW = this.title?.w ?? 160
        this.titleAt = { x: cx - Math.round(titleW / 2), y: TITLE_CAP_Y - capTop }
        this.rule = { cx, y: this.titleAt.y + base + 6, half: Math.min(Math.round(titleW / 2) + 22, 160) }
        this.overline?.position.set(cx * ART_PX, (TITLE_CAP_Y - 6) * ART_PX)
        const k = this.wordScale
        if (this.line) {
            // However large the line is drawn, it keeps within the words' box, clear of the moon.
            this.line.style.wordWrapWidth = Math.min(LINE_WRAP * k, (WORDS_BOX.x1 - WORDS_BOX.x0) * ART_PX, width - 48)
            this.line.position.set(cx * ART_PX, (this.rule.y + 6) * ART_PX)
        }
        const begin = this.button
        if (!begin) return
        const right = x0 + w - PLATE_EDGE
        const bottom = y0 + h - PLATE_EDGE
        const later = this.laterButton
        const gap = Math.round(PLATE_GAP * k)
        for (const plate of [begin, later]) {
            if (!plate) continue
            plate.w = Math.round(plate.size.w * k)
            plate.h = Math.round(plate.size.h * k)
        }
        begin.x = right - begin.w
        if (later && width < STACK_BELOW * k) {
            begin.y = bottom - begin.h * 2 - gap
            later.x = right - later.w
            later.y = bottom - later.h
        } else {
            begin.y = bottom - begin.h
            if (later) {
                later.x = begin.x - gap - later.w
                later.y = begin.y
            }
        }
        for (const plate of [begin, later]) {
            if (!plate) continue
            plate.hit.position.set(plate.x * ART_PX, plate.y * ART_PX)
            plate.hit.hitArea = new Rectangle(0, 0, plate.w * ART_PX, plate.h * ART_PX)
        }
        const middle = (plate: Plate): [number, number] => [(plate.x + plate.w / 2) * ART_PX, (plate.y + plate.h / 2) * ART_PX + 0.5]
        this.beginLit?.position.set(...middle(begin))
        this.beginUnlit?.position.set(...middle(begin))
        if (later) this.laterCaption?.position.set(...middle(later))
    }

    isOpen = (): boolean => this.visible

    /** True while Begin and Later will answer: from the first frame until one of them has. */
    isReady = (): boolean => this.visible && !this.exit

    /** True once everything has arrived. */
    isSettled = (): boolean => this.visible && cardPose(this.ms).settled

    begin = () => {
        if (!this.isReady() || !this.canBegin()) return
        this.exit = { kind: "begin", ms: 0 }
        this.onCue("launch")
    }

    maybeLater = () => {
        if (!this.isReady() || (this.chapter || 1) < 2 || !this.canBegin()) return
        this.exit = { kind: "later", ms: 0 }
    }

    close = () => {
        this.chapter = undefined
        this.visible = false
        this.exit = undefined
    }

    animate = (dtMs: number) => {
        if (!this.visible) return
        const dt = Math.max(0, Math.min(MAX_STEP_MS, dtMs || 0))
        this.ms += dt
        this.clock += dt
        if (this.exit) this.exit.ms += dt
        if (!this.cueRevealed && this.ms >= CARD.titleAt) {
            this.cueRevealed = true
            this.onCue("reveal")
        }
        this.pose()
        const moving = !cardPose(this.ms).settled || !!this.exit
        if (moving || this.clock - this.paintedAt >= REST_REPAINT_MS) this.paint()
        const exit = this.exit
        if (exit && (exit.kind === "begin" ? beginPose(exit.ms) : laterPose(exit.ms)).done) {
            const chapter = this.chapter
            this.close()
            if (chapter !== undefined) {
                if (exit.kind === "later") this.onLater?.(chapter)
                else this.onBegin(chapter)
            }
        }
    }

    // ---- The words --------------------------------------------------------------------------

    private wordsAlpha = () => {
        if (!this.exit) return 1
        return this.exit.kind === "begin" ? beginPose(this.exit.ms).words : laterPose(this.exit.ms).words
    }

    private pose = () => {
        const pose = cardPose(this.ms)
        const words = this.wordsAlpha()
        this.alpha = this.exit?.kind === "later" ? laterPose(this.exit.ms).cover : 1
        if (this.overline) {
            this.overline.alpha = pose.overlineAlpha * words
            this.overline.pivot.y = -pose.overlineRise
        }
        if (this.line) {
            this.line.alpha = pose.lineAlpha * words
            this.line.pivot.y = -pose.lineRise
        }
        this.captions.alpha = pose.actionsAlpha * words
        // Begin's caption: ink where the plate is lit, cream where it is not yet.
        const begin = this.button
        if (!begin) return
        const left = begin.x * ART_PX
        const front = (begin.x + begin.w * pose.actions.ignite) * ART_PX
        const right = (begin.x + begin.w) * ART_PX
        const top = begin.y * ART_PX
        const height = begin.h * ART_PX
        this.litClip.clear().beginFill(0xffffff).drawRect(left, top, Math.max(0, front - left), height).endFill()
        this.unlitClip.clear().beginFill(0xffffff).drawRect(front, top, Math.max(0, right - front), height).endFill()
        if (this.laterCaption) this.laterCaption.tint = this.laterButton?.hovered ? TEXT : TEXT_SOFT
    }

    // ---- The picture ------------------------------------------------------------------------

    /** The view, the title and rule, the plates, and the dissolves in and out, onto the layer. */
    private paint = () => {
        const { view, buffer } = this
        if (!view || !buffer) return
        this.paintedAt = this.clock
        const pixels = buffer.data
        view.paint(pixels, this.clock)
        const pose = cardPose(this.ms)
        const fade = 1 - this.wordsAlpha()
        const canvas = { out: pixels, x0: buffer.x0, y0: buffer.y0, w: buffer.w, h: buffer.h }
        if (this.title && this.glow) paintTitle(canvas, this.title, this.glow, this.titleAt.x, this.titleAt.y, pose.titleMs, fade)
        paintRule(canvas, this.rule.cx, this.rule.y, this.rule.half, pose.rule, fade)
        this.paintPlates(buffer, pose, fade)

        // Arriving: black until the sky comes up, then each ridge shows the sky until it is in.
        // Leaving by Begin: the view dissolves in toward the firelight, uncovering the world.
        const arriving = this.ms < CARD.skyMs * 0.4 + view.layers * CARD.ridgeStepMs + CARD.ridgeFadeMs
        const dissolve = this.exit?.kind === "begin" ? beginPose(this.exit.ms).dissolve : 0
        if (arriving || dissolve > 0) {
            const far = dissolve > 0 ? view.distanceFromFire() : undefined
            for (let j = 0, i = 0; j < buffer.h; j++) {
                const y = buffer.y0 + j
                for (let k = 0; k < buffer.w; k++, i++) {
                    const b = bayer(buffer.x0 + k, y)
                    if (arriving) {
                        if (!shownAt(this.ms, LAYER_SKY, b)) pixels[i] = BLACK
                        else if (!shownAt(this.ms, view.layer[i], b)) pixels[i] = view.sky[i]
                    }
                    if (far && dissolve > b * 0.45 + (1 - far[i]) * 0.55) pixels[i] = 0
                }
            }
        }
        this.layer.commit(buffer)
    }

    /**
     * Begin, the builder's amber plate: dark until it lights from the left, a bright edge leading the
     * light across, then one sheen and stillness. Later, a dark tile. Both arrive with the card,
     * dissolved in, and go with the words.
     */
    private paintPlates = (buf: PixelBuffer, pose: ReturnType<typeof cardPose>, fade: number) => {
        const shown = pose.actionsAlpha * (1 - fade)
        const cta = pose.actions
        const plate = (p: Plate, lit: (i: number) => boolean) => {
            for (let j = 0; j < p.h; j++) {
                for (let i = 0; i < p.w; i++) {
                    if ((i === 0 || i === p.w - 1) && (j === 0 || j === p.h - 1)) continue
                    const x = p.x + i, y = p.y + j
                    if (shown < 1 && shown <= bayer(x, y)) continue
                    const rim = i === 0 || j === 0 || i === p.w - 1 || j === p.h - 1
                    let c: number
                    if (lit(i)) c = rim ? PLATE.amberLine : j === 1 ? PLATE.amberHi : j === p.h - 2 ? PLATE.amberLo : p.hovered ? PLATE.amberHover : PLATE.amber
                    else c = rim ? (p.hovered ? PLATE.bronze : PLATE.edge) : p.hovered ? PLATE.tileHover : PLATE.tile
                    buf.set(x, y, c)
                }
            }
        }
        const begin = this.button
        if (begin) {
            const front = begin.w * cta.ignite
            plate(begin, i => i < front)
            // The edge leading the light across, and the one sheen once it has landed.
            if (cta.edge > 0 && shown > 0) {
                const ex = begin.x + Math.round(front)
                for (let j = 1; j < begin.h - 1; j++) buf.blend(ex, begin.y + j, PLATE.light, cta.edge)
            }
            if (cta.shineT >= 0 && !this.exit) {
                const at = begin.x - 6 + (begin.w + 12) * cta.shineT
                for (let j = 1; j < begin.h - 1; j++) {
                    for (let i = 1; i < begin.w - 1; i++) {
                        const d = Math.abs(begin.x + i + (j - begin.h / 2) * 0.6 - at)
                        if (d < 3) buf.blend(begin.x + i, begin.y + j, PLATE.amberHi, d < 1.5 ? 0.55 : 0.25)
                    }
                }
            }
        }
        if (this.laterButton) plate(this.laterButton, () => false)
    }

    destroy = () => {
        this.clearContent()
        this.layer.dispose()
        super.destroy({ children: true })
    }
}
